import { FormLabelWithOptional } from 'components/form/FormLabelWithOptional';
import { JFormInput } from 'components/form/JFormInput';
import { GridRow } from 'components/GridRow';
import { MFormCheckbox } from 'components/MeridianFinalFormWrappers/MFormCheckbox';
import { useMemo } from 'react';
import { Collapse, FormText } from 'react-bootstrap';
import { useField } from 'react-final-form';
import { stylesheet } from 'typestyle';
import { useGetFieldPath } from 'utils/pathing';
import { colorValidate } from '../../../generic.util';
import type { BtfRow, BtfRowStyles } from '../../types';
import { BtfColorPreview } from '../BtfColorPreview';

const css = stylesheet({
  backgroundColorContainer: {
    display: 'flex',
  },
  backgroundColorInput: {
    flexGrow: 1,
  },
  backgroundColorPreview: {
    alignSelf: 'center',
    marginTop: '4px',
    padding: '8px',
  },
  marginsContainer: {
    display: 'grid',
    gap: '0.75rem',
    gridTemplateColumns: 'repeat(2, minmax(80px, min-content))',
    whiteSpace: 'nowrap',
  },
  maxWidthAndRowIdContainer: {
    $nest: {
      '&>*': {
        flex: 1,
        maxWidth: '250px',
        minWidth: '190px',
      },
    },
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
  },
});

export function CommonTemplateProperties({ name }: { name: string }) {
  const gp = useGetFieldPath<BtfRow>(name);
  const {
    input: { value: rowStyles },
  } = useField<BtfRowStyles>(gp('rowStyles'), {
    subscription: {
      value: true,
    },
  });
  const {
    input: { value: rowBackgroundColor, onChange: onRowBackgroundColorChange },
  } = useField<string | undefined>(gp('rowStyles.backgroundColor'));

  const handleBackgroundColorChange = (value: string) => {
    const prependedValue = `#${value}`;
    if (CSS.supports('color', prependedValue)) {
      onRowBackgroundColorChange(prependedValue);
    }
  };

  const hasNegativeMargins = useMemo(() => {
    const { marginTop, marginBottom } = rowStyles;
    // Note 0 is falsy, -N is truthy, and +N is truthy.
    // This only returns true if one of the margins is defined, not null, and negative.
    return (marginTop && marginTop < 0) || (marginBottom && marginBottom < 0);
  }, [
    rowStyles,
  ]);

  return (
    <section>
      <h5>Row properties</h5>
      <p>These properties apply to the entire row container.</p>
      <FormLabelWithOptional label="Margins" optional />
      <div className={css.marginsContainer}>
        <JFormInput label="Top (px)" name={gp('rowStyles.marginTop')} placeholder="0" step={1} type="number" />
        <JFormInput label="Bottom (px)" name={gp('rowStyles.marginBottom')} placeholder="0" step={1} type="number" />
      </div>
      {hasNegativeMargins && (
        <FormText className="text-danger mb-3">Warning! Using negative margins may cause issues with layout, links, and more.</FormText>
      )}
      <div className={css.maxWidthAndRowIdContainer}>
        <JFormInput
          label="Row max width (px)"
          min={0}
          name={gp('rowStyles.rowMaxWidth')}
          optional
          placeholder="1500"
          step={1}
          type="number"
        />
        <JFormInput
          description={`"Row ID" defines the HTML ID that will be
                        applied to the row when rendered on the
                        page.`}
          label="Row ID"
          name={gp('rowId')}
          optional
          placeholder="custom HTML ID"
        />
      </div>
      <hr />
      <MFormCheckbox
        label="Add background color (enables padding, content max width, and rounded corners)"
        name={gp('rowStyles.hasBackground')}
      />
      <Collapse in={!!rowStyles.hasBackground}>
        <div
          style={{
            paddingLeft: '1.25rem',
          }}
        >
          <GridRow minItemWidth="200px">
            <div className={css.backgroundColorContainer}>
              <JFormInput
                className={css.backgroundColorInput}
                label="Background color"
                name={gp('rowStyles.backgroundColor')}
                onChange={handleBackgroundColorChange}
                optional
                placeholder="#FFFFFF"
                validate={(value: string) => colorValidate(value, !!rowStyles.hasBackground)}
              />
              <div className={css.backgroundColorPreview}>
                <BtfColorPreview color={rowBackgroundColor} />
              </div>
            </div>
            <JFormInput
              label="Content max width (px)"
              min={0}
              name={gp('rowStyles.contentMaxWidth')}
              optional
              placeholder="1500"
              step={1}
              type="number"
            />
          </GridRow>

          <FormLabelWithOptional label="Space around content/padding" optional />
          <GridRow minItemWidth="80px">
            <JFormInput label="Top (px)" min={0} name={gp('rowStyles.paddingTop')} placeholder="0" step={1} type="number" />
            <JFormInput label="Right (px)" min={0} name={gp('rowStyles.paddingRight')} placeholder="0" step={1} type="number" />
            <JFormInput label="Bottom (px)" min={0} name={gp('rowStyles.paddingBottom')} placeholder="0" step={1} type="number" />
            <JFormInput label="Left (px)" min={0} name={gp('rowStyles.paddingLeft')} placeholder="0" step={1} type="number" />
          </GridRow>

          <FormLabelWithOptional label="Rounded corners" optional />
          <GridRow minItemWidth="80px">
            <JFormInput
              label="⌜ Top left (px)"
              min={0}
              name={gp('rowStyles.borderRadius.topLeft')}
              placeholder="0"
              step={1}
              type="number"
            />
            <JFormInput
              label="⌝ Top right (px)"
              min={0}
              name={gp('rowStyles.borderRadius.topRight')}
              placeholder="0"
              step={1}
              type="number"
            />
            <JFormInput
              label="⌟ Bottom right (px)"
              min={0}
              name={gp('rowStyles.borderRadius.bottomRight')}
              placeholder="0"
              step={1}
              type="number"
            />
            <JFormInput
              label="⌞ Bottom left (px)"
              min={0}
              name={gp('rowStyles.borderRadius.bottomLeft')}
              placeholder="0"
              step={1}
              type="number"
            />
          </GridRow>
        </div>
      </Collapse>
    </section>
  );
}
